import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Form } from 'semantic-ui-react';
import { RESPONSE_STATUS } from '../../../../constnats/general';
import { TASK_PRIORITIES, TASK_STATUSES } from '../../../../constnats/task';
import useFullModalState from '../../../../hooks/useFullModalState';
import { TaskExecutionInfoType, TaskType } from '../../../../schemas/task.schemas';
import { UserType } from '../../../../types/user.type';
import Alert from '../../../../utils/alert';
import TasksRepo from '../../../../utils/repository/tasksRepo/tasksRepo';
import ConfirmationModal from '../../../confirmationModal';
import DatePicker from '../../../form/datePicker';
import { getOption } from '../utils';
import { getTaskFormDefaultState, taskFormSchema, TaskFormSchemaType } from './utils';
import "../form.styles.css"

type TaskFormPropsType = {
    task?: Partial<TaskType>;
    users: UserType[];
    onSave: (task: TaskFormSchemaType) => void;
    onCancel?: () => void;
    executions?: TaskExecutionInfoType[];
    readOnly?: boolean;
};

function TaskForm({ task, users, onSave, onCancel, executions, readOnly }: TaskFormPropsType) {
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<TaskFormSchemaType>({
        defaultValues: getTaskFormDefaultState(task),
        resolver: zodResolver(taskFormSchema),
    });

    const deleteModalState = useFullModalState();

    const onSubmit = handleSubmit((data) => {
        onSave(data);
    });

    const isStatusDisabled = !!executions?.length;

    const navigate = useNavigate();

    const deleteTask = () => {
        deleteModalState.openModal(task?.id);
    };

    return (
        <>
            <Form onSubmit={onSubmit} className="task__form">
                <Controller
                    disabled={readOnly}
                    name="title"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <Form.Input
                            disabled={readOnly}
                            value={value}
                            onChange={(_, data) => onChange(data.value)}
                            fluid
                            label="Title"
                            placeholder="Create Margin Field"
                            error={errors.title?.message}
                        />
                    )}
                />
                <Form.Group widths={'equal'}>
                    <Controller
                        disabled={readOnly}
                        name="priority"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <Form.Dropdown
                                disabled={readOnly}
                                value={value}
                                onChange={(_, data) => onChange(data.value)}
                                label="Priority"
                                placeholder="Priority"
                                selection
                                search
                                options={TASK_PRIORITIES.map((s) => getOption(s, s))}
                                error={errors.priority?.message}
                            />
                        )}
                    />
                    {task?.id && (
                        <Controller
                            disabled={readOnly}
                            name="status"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <Form.Dropdown
                                    disabled={isStatusDisabled || readOnly}
                                    value={value}
                                    onChange={(_, data) => onChange(data.value)}
                                    label="Status"
                                    placeholder="Status"
                                    selection
                                    search
                                    options={TASK_STATUSES.map((s) => getOption(s, s))}
                                    error={errors.status?.message}
                                />
                            )}
                        />
                    )}
                </Form.Group>
                <Form.Group widths={'equal'}>
                    <Controller
                        disabled={readOnly}
                        name="assignedUserId"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <Form.Dropdown
                                disabled={readOnly}
                                value={value ?? ''}
                                onChange={(_, data) => onChange(data.value || null)}
                                label="Assignee"
                                placeholder="Assignee"
                                selection
                                search
                                options={[getOption('', 'No user'), ...users.map((u) => getOption(u.id, u.fullName))]}
                                error={errors.assignedUserId?.message}
                            />
                        )}
                    />
                    <Controller
                        disabled={readOnly}
                        name="dueDate"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <DatePicker
                                disabled={readOnly}
                                date={new Date(value)}
                                label="Due Date"
                                onChange={(date: Date) => onChange(date.toISOString())}
                            />
                        )}
                    />
                </Form.Group>
                <Controller
                    disabled={readOnly}
                    name="description"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <Form.TextArea
                            readOnly={readOnly}
                            value={value}
                            onChange={(_, data) => onChange(data.value)}
                            label="Description"
                            error={errors.description?.message}
                            rows={8}
                        />
                    )}
                />
                {task?.id && (
                    <Form.Dropdown
                        disabled={true}
                        value={task.createdByUserId}
                        label="Created By"
                        selection
                        options={[getOption('', 'No user'), ...users.map((u) => getOption(u.id, u.fullName))]}
                    />
                )}

                <Form.Group className="flex flex-justify-space-between">
                    <Form.Button disabled={readOnly} primary type="submit">
                        Save
                    </Form.Button>

                    {task?.id ? (
                        <Form.Button disabled={readOnly} type="button" color="red" onClick={deleteTask}>
                            Delete
                        </Form.Button>
                    ) : (
                        <Form.Button type="button" onClick={onCancel}>
                            Cancel
                        </Form.Button>
                    )}
                </Form.Group>
            </Form>

            <ConfirmationModal
                msg="Are you sure you want to delete this task?"
                onCancel={deleteModalState.closeModal}
                open={deleteModalState.open}
                onConfirm={() => {
                    TasksRepo.deleteTask(task?.id!, (response) => {
                        if (response.status === RESPONSE_STATUS.SUCCESS) {
                            Alert.success('Task deleted successfully!');
                            deleteModalState.closeModal();
                            navigate('/admin/tasks');
                        } else {
                            Alert.warn(response.message);
                        }
                    });
                }}
            />
        </>
    );
}

export default TaskForm;
